import { Injectable, OnDestroy } from '@angular/core';
import { WindowRef } from '@spartacus/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface SubmenuStateConfig {
  isMobile: boolean;
  leaveTimeoutDelay?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SubmenuStateService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly openMenuItemSubject = new BehaviorSubject<string | null>(null);
  private readonly isMobileSubject = new BehaviorSubject<boolean>(false);
  private leaveTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private resizeObserver?: ResizeObserver;
  private readonly defaultLeaveTimeoutDelay = 200;

  constructor(private windowRef: WindowRef) {
    this.initializeResizeObserver();
  }

  /**
   * Observable that emits the currently open menu item UID
   */
  get openMenuItemUid$(): Observable<string | null> {
    return this.openMenuItemSubject.asObservable();
  }

  /**
   * Observable that emits the current mobile state
   */
  get isMobile$(): Observable<boolean> {
    return this.isMobileSubject.asObservable();
  }

  /**
   * Get the current open menu item UID
   */
  get currentOpenMenuItemUid(): string | null {
    return this.openMenuItemSubject.value;
  }

  /**
   * Get the current mobile state
   */
  get isMobile(): boolean {
    return this.isMobileSubject.value;
  }

  /**
   * Check if a specific submenu is open
   */
  isSubmenuOpen(menuItemUid: string): boolean {
    return this.currentOpenMenuItemUid === menuItemUid;
  }

  /**
   * Toggle a submenu open/closed
   */
  toggleSubmenu(menuItemUid: string, config?: Partial<SubmenuStateConfig>): void {
    const effectiveConfig = {
      isMobile: this.isMobile,
      ...config,
    };

    if (this.currentOpenMenuItemUid === menuItemUid) {
      this.closeOpenMenu();
    } else {
      this.openSubmenu(menuItemUid);
    }
  }

  /**
   * Open a specific submenu
   */
  openSubmenu(menuItemUid: string): void {
    this.clearLeaveTimeout();
    this.openMenuItemSubject.next(menuItemUid);
  }

  /**
   * Close the currently open submenu
   */
  closeOpenMenu(): void {
    this.openMenuItemSubject.next(null);
  }

  /**
   * Handle mouse enter events for menu items
   */
  handleItemMouseEnter(menuItemUid: string, config?: Partial<SubmenuStateConfig>): void {
    const effectiveConfig = {
      isMobile: this.isMobile,
      ...config,
    };

    // Skip mouse hover behavior on mobile devices
    if (effectiveConfig.isMobile) {
      return;
    }

    this.clearLeaveTimeout();

    if (this.currentOpenMenuItemUid !== menuItemUid) {
      this.openSubmenu(menuItemUid);
    }
  }

  /**
   * Handle mouse leave events for menu items
   */
  handleItemMouseLeave(menuItemUid: string, config?: Partial<SubmenuStateConfig>): void {
    const effectiveConfig = {
      isMobile: this.isMobile,
      leaveTimeoutDelay: this.defaultLeaveTimeoutDelay,
      ...config,
    };

    // Skip mouse hover behavior on mobile devices
    if (effectiveConfig.isMobile) {
      return;
    }

    this.clearLeaveTimeout();

    this.leaveTimeoutId = setTimeout(() => {
      this.closeOpenMenu();
      this.leaveTimeoutId = null;
    }, effectiveConfig.leaveTimeoutDelay);
  }

  /**
   * Clear any pending leave timeout
   */
  clearLeaveTimeout(): void {
    if (this.leaveTimeoutId) {
      clearTimeout(this.leaveTimeoutId);
      this.leaveTimeoutId = null;
    }
  }

  /**
   * Force close all menus (useful for escape key, document clicks, etc.)
   */
  forceCloseAllMenus(): void {
    this.clearLeaveTimeout();
    this.closeOpenMenu();
  }

  /**
   * Check if click target is outside a given element
   */
  isClickOutside(event: MouseEvent, containerElement: Element): boolean {
    return !containerElement.contains(event.target as Node);
  }

  private initializeResizeObserver(): void {
    if (this.windowRef.isBrowser()) {
      this.checkIfMobile();

      this.resizeObserver = new ResizeObserver(() => {
        this.checkIfMobile();
      });
      this.resizeObserver.observe(document.body);
    }
  }

  private checkIfMobile(): void {
    if (!this.windowRef.isBrowser()) {
      return;
    }

    const previousIsMobile = this.isMobile;
    const currentIsMobile = window.innerWidth < 1024;

    this.isMobileSubject.next(currentIsMobile);

    // If transitioning from desktop to mobile, close any open menus
    if (!previousIsMobile && currentIsMobile && this.currentOpenMenuItemUid) {
      this.closeOpenMenu();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.clearLeaveTimeout();
  }
}
